import { useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";
import { CONTACT } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Quote = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "", aircraft: "", location: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(t.quote.form.success);
      setForm({ name: "", email: "", phone: "", company: "", service: "", aircraft: "", location: "", message: "" });
    } catch {
      toast.error(t.quote.form.error);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-[#020617] border border-white/15 rounded-lg px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] transition-all duration-300";

  return (
    <section id="quote" data-testid="quote-section" className="py-28 md:py-36 border-t border-white/10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#60A5FA] mb-6" data-testid="quote-overline">
            {t.quote.overline}
          </p>
          <h2 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-6xl max-w-3xl" data-testid="quote-title">
            {t.quote.title}
          </h2>
          <p className="mt-6 max-w-xl text-slate-400 text-base leading-relaxed">{t.quote.body}</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <div data-testid="quote-aog-card" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 relative overflow-hidden">
              <span className="absolute top-6 right-6 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
              <PhoneCall size={26} className="text-red-400 mb-5" />
              <h3 className="font-display font-bold text-2xl tracking-tight mb-2" data-testid="quote-aog-title">{t.quote.aogTitle}</h3>
              <p className="text-slate-400 text-sm mb-6">{t.quote.aogBody}</p>
              <a
                data-testid="quote-aog-call-link"
                href={`tel:${CONTACT.phoneHref}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-medium text-sm transition-all duration-300 hover:scale-[1.04]"
              >
                {CONTACT.phone}
              </a>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            data-testid="quote-form"
            className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0F172A]/60 p-8 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <input data-testid="quote-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.quote.form.name} className={field} />
              <input data-testid="quote-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.quote.form.email} className={field} />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <input data-testid="quote-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.quote.form.phone} className={field} />
              <input data-testid="quote-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t.quote.form.company} className={field} />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="relative">
                <select
                  data-testid="quote-service-select"
                  required
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className={`${field} appearance-none pr-10 ${form.service ? "" : "text-slate-500"}`}
                >
                  <option value="" disabled>{t.quote.form.service}</option>
                  {t.services.items.map((s, i) => (
                    <option key={i} value={s.title}>{s.title}</option>
                  ))}
                  <option value="other">{t.quote.form.other}</option>
                </select>
                <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
              <input data-testid="quote-aircraft-input" value={form.aircraft} onChange={(e) => setForm({ ...form, aircraft: e.target.value })} placeholder={t.quote.form.aircraft} className={field} />
            </div>
            <input data-testid="quote-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t.quote.form.location} className={field} />
            <textarea data-testid="quote-message-input" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.quote.form.message} className={`${field} resize-none`} />
            <button
              data-testid="quote-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full md:w-auto px-8 py-3.5 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-medium transition-all duration-300 hover:scale-[1.03]"
            >
              {sending ? t.quote.form.sending : t.quote.form.submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
